<template>
  <div :class="[additionalClasses, 'variety-comparison my-4']">
    <button class="button button--link" @click="goBack">{{ translate('lblBackToVarietySelector') }}</button>

    <div :class="['comparison-table py-4 my-4', `varieties-${result.products.length}`]" v-if="result && result.products">
      <div class="full-width-on-mobile mb-5">
        <div class="horizontal-scrolling">
          <table>
            <tbody v-for="(rowGroup, rowGroupIndex) in result.tables" :key="rowGroupIndex">
              <tr v-if="rowGroup.title" class="title">
                <td v-text="rowGroup.title" class="fixed"></td>
                <td :colspan="result.products.length">&nbsp;</td>
              </tr>
              <tr v-for="(row, rowIndex) in rowGroup.rows" :key="`${rowGroupIndex}-${rowIndex}`">
                <td v-for="(col, colIndex) in row" :key="colIndex"
                    :class="[col.style, colIndex === 0 ? 'fixed' : '']"
                    v-html="humanize(col.value, colIndex - 1)"
                ></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <button class="button button--link" @click="goBack">{{ translate('lblBackToVarietySelector') }}</button>
    </div>

    <notification-modal
      v-if="error"
      type="error"
      :title="translate('lblErrorTitle')"
      :subtitle="error.subtitle"
      :content="error.content"
      :redirect-url="varietySelectorErrorLink"
      :prevent-close="true"
    ></notification-modal>

    <loading-indicator v-if="loading"></loading-indicator>
  </div>
</template>

<script>
import Vue from 'vue';
import ProductImageLink from '@/variety-advisor/components/product-image-link.vue';
import TextLink from '@/variety-advisor/components/text-link.vue';
import ProductImage from '@/variety-advisor/components/product-image.vue';
import NotificationModal from '../shared/components/NotificationModal.vue';
import LoadingMixin from '../shared/mixins/LoadingMixin';

export default {
  name: 'VarietyComparison',
  data: () => ({
    error: null,
    comparisonIds: [],
    result: {},
    crop: null,
  }),
  props: {
    additionalClasses: String,
    translations: Object,
    apiUrl: {
      type: String,
      required: true,
    },
    crops: {
      type: Object,
      required: true,
    },
    language: {
      type: String,
      required: true,
    },
    varietySelectorErrorLink: {
      type: String,
      required: true,
    },
  },
  computed: {
    sizes() {
      const breakpoints = {
        xl: {
          viewportWidth: '1280px',
          containerWidths: '1280px',
          paddings: '7.25rem',
          colNumbers: 4,
        },
        l: {
          viewportWidth: '1024px',
          containerWidths: '100vw',
          paddings: '7.25rem',
          colNumbers: 4,
        },
        m: {
          viewportWidth: '768px',
          containerWidths: '100vw',
          paddings: '4.875rem',
          colNumbers: 4,
        },
        s: {
          containerWidths: '100vw',
          paddings: '1.6875rem',
          colNumbers: 2,
        },
      };
      return Object.values(breakpoints).map((breakpoint) => {
        const size = [];
        if (breakpoint.viewportWidth) {
          size.push(`(min-width: ${breakpoint.viewportWidth}) `);
        }
        size.push(`calc((${breakpoint.containerWidths} - (2 * ${breakpoint.paddings})) / ${breakpoint.colNumbers})`);
        return size.join('');
      }).join(', ');
    },
  },
  methods: {
    goBack() {
      window.history.back();
    },
    humanize(value, productIndex) {
      switch (typeof value) {
        case 'boolean':
          return this.translate(value ? 'lblYes' : 'lblNo');
        case 'undefined':
          return this.translate('lblNull');
        case 'object':
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          if (value === null) {
            return this.translate('lblNull');
          }
          if (Object.keys(value).some(
            (resolution) => /[a-z]+-(\d+)/i.test(resolution),
          )) {
            // it's an image
            const Component = this.result.products[productIndex].url ? Vue.extend(ProductImageLink) : Vue.extend((ProductImage));
            const productImage = new (Component)({
              propsData: {
                resolutions: value,
                isNew: (productIndex >= 0 && this.result.products[productIndex].new),
                language: this.language,
                sizes: this.sizes,
                link: this.result.products[productIndex].url,
              },
            });
            productImage.$mount();
            return productImage.$el.outerHTML;
          }
          console.warn('unknown object found');
          return value.toString();
        case 'string':
          // It's the title
          if (value === this.result.products[productIndex]?.variety) {
            if (!this.result.products[productIndex].url) return value;
            const link = new (Vue.extend(TextLink))({
              propsData: {
                text: value,
                link: this.result.products[productIndex].url,
              },
            });
            link.$mount();
            return link.$el.outerHTML;
          }
          return value;
        case 'number':
          return value;
        default:
          return '';
      }
    },
    getFromUrl(paramName, defaultValue) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const jsonString = urlSearchParams.get(paramName);
      if (jsonString) {
        return JSON.parse(jsonString);
      }
      return defaultValue;
    },
    addBasicInformationRows() {
      const basicInformationRows = [];
      basicInformationRows.push([
        { value: '', style: 'image' },
        ...this.result.products.map((p) => ({ value: p.image, style: 'image' })),
      ]);
      basicInformationRows.push([
        { value: this.translate('lblVarietyName'), style: 'variety' },
        ...this.result.products.map((p) => ({ value: p.variety, style: 'variety' })),
      ]);

      if (this.result.products.some((p) => !!p.fao)) {
        basicInformationRows.push([
          { value: this.translate('lblMaturity') },
          ...this.result.products.map((p) => ({ value: p.fao })),
        ]);
      }

      if (!this.result.tables || !this.result.tables.length) {
        this.result.tables = [{}];
      }
      if (!this.result.tables[0].rows) {
        this.result.tables[0].rows = [];
      }
      this.result.tables[0].rows.unshift(...basicInformationRows);
    },
    async loadResult() {
      this.startRequest();
      const result = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          query: this.crops[this.crop],
          variables: {
            ids: this.comparisonIds,
          },
        }),
      })
        .then((response) => response.json())
        .catch(() => {
          this.error = {
            subtitle: this.translate('lblNetworkErrorSubTitle'),
            content: this.translate('lblNetworkErrorText'),
          };
        })
        .finally(() => {
          this.finishRequest();
        });

      this.result = result?.data?.comparison;

      if (this.result) {
        this.addBasicInformationRows();
      } else {
        const content = result?.errors
          ? result.errors.map((e) => e.message).join(' ')
          : this.translate('lblNetworkErrorText');
        this.error = {
          subtitle: this.translate('lblNetworkErrorSubTitle'),
          content,
        };
      }
    },
  },
  async created() {
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    this.comparisonIds = this.getFromUrl('comparison-ids', []);
    this.crop = this.getFromUrl('crop', null);
    if (this.comparisonIds.length < 2 || !this.crop || !this.crops[this.crop]) {
      this.error = {
        subtitle: this.translate('lblDataErrorSubTitle'),
        content: this.translate('lblDataErrorText'),
      };
    } else {
      await this.loadResult();
    }
  },
  mixins: [LoadingMixin],
  components: {
    NotificationModal,
  },
};
</script>

<style scoped lang="scss">
$border-width: 1px;

$number-of-cols: (
  s: 2,
  m: 4,
  l: 4,
  xl: 4,
) !default;

$container-widths: (
  s: 100vw,
  m: 100vw,
  l: 100vw,
  xl: 1280px
) !default;

.variety-comparison {
  width: 100%;
}

.comparison-table {
  width: 100vw;
  margin-right: -$main-padding-s;
  margin-left: -$main-padding-s;
  padding-right: $main-padding-s;
  padding-left: $main-padding-s;
  background-color: $color-white;

  .bg-white & {
    background-color: $color-kws-black-5;
  }

  @each $breakpoint in (m, l) {
    @include media-breakpoint-up($breakpoint) {
      $main-padding: map-get($main-paddings, $breakpoint);
      width: calc(100% + 2 * #{rem($main-padding)});
      margin-right: -$main-padding;
      margin-left: -$main-padding;
      padding-right: $main-padding;
      padding-left: $main-padding;
    }
  }
}
.full-width-on-mobile {
  width: 100vw;
  margin-right: -$main-padding-s;
  margin-left: -$main-padding-s;

  @include media-breakpoint-up(m) {
    width: 100%;
    margin-right: 0;
    margin-left: 0;
  }
}

.horizontal-scrolling {
  overflow: hidden;
  overflow-x: scroll;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
}

table {
  border-collapse: separate;
  border-spacing: rem($border-width);
}

tr.title td {
  background-color: $color-white;
  font-weight: 700;
  padding-top: rem(20px);
}

td {
  padding: rem(10px) rem(16px);
  border-inline-end: rem($border-width) solid $color-white;
  vertical-align: top;
  background-color: $color-kws-black-5;

  .bg-white & {
    background-color: $color-white;
  }

  &.image {
    background-color: $color-white;
    padding: 0;
    text-align: center;
    vertical-align: middle;
  }

  &.variety {
    font-weight: 700;
  }

  &.fixed {
    position: -webkit-sticky;
    position: sticky;
    inset-inline-start: 0;
    top: auto;
    word-break: break-word;
    z-index: 3;
  }
}

$col-width: calc(((#{map-get($container-widths, 's')} - (2 * #{rem(map-get($main-paddings, 's'))})) / #{map-get($number-of-cols, 's')}) - ((#{map-get($number-of-cols, 's')} - 1) * #{rem($border-width)}));

td {
  hyphens: auto;
  min-width: calc(((#{map-get($container-widths, 's')} - (2 * #{rem(map-get($main-paddings, 's'))})) / #{map-get($number-of-cols, 's')}) - ((#{map-get($number-of-cols, 's')} - 1) * #{rem($border-width)}));
  max-width: calc(((#{map-get($container-widths, 's')} - (2 * #{rem(map-get($main-paddings, 's'))})) / #{map-get($number-of-cols, 's')}) - ((#{map-get($number-of-cols, 's')} - 1) * #{rem($border-width)}));

  &:not(.fixed) {
    .varieties-2 & {
      @include media-breakpoint-up('m') {
        min-width: unset;
        max-width: unset;
        width: 37.5%;
      }
    }
    .varieties-3 & {
      @include media-breakpoint-up('m') {
        min-width: unset;
        max-width: unset;
        width: 25%;
      }
    }
  }

  &:nth-child(2) {
    margin-inline-start: calc(((#{map-get($container-widths, 's')} - (2 * #{rem(map-get($main-paddings, 's'))})) / #{map-get($number-of-cols, 's')}) - ((#{map-get($number-of-cols, 's')} - 1) * #{rem($border-width)}) + #{rem($border-width)});
  }
}

@each $breakpoint in (m, l, xl) {
  @include media-breakpoint-up($breakpoint) {
    $container-width: map-get($container-widths, $breakpoint);
    $main-padding: map-get($main-paddings, $breakpoint);
    $number-of-col: map-get($number-of-cols, $breakpoint);

    td {
      min-width: calc(((#{$container-width} - (2 * #{rem($main-padding)})) / #{$number-of-col}) - ((#{$number-of-col} - 1) * #{rem($border-width)}));
      max-width: calc(((#{$container-width} - (2 * #{rem($main-padding)})) / #{$number-of-col}) - ((#{$number-of-col} - 1) * #{rem($border-width)}));

      &:nth-child(2) {
        margin-inline-start: calc(((#{$container-width} - (2 * #{rem($main-padding)})) / #{$number-of-col}) - ((#{$number-of-col} - 1) * #{rem($border-width)}) + #{rem($border-width)});
      }
    }
  }
}
</style>
